<template>
  <div class="wrap">
    <header>
      <h1>搞懂 GPT 账号的网络用词</h1>
      <div class="sub">小白新手必看：AT、RT、OAuth、已接码、CPA、Sub2API、Cockpit 一次讲清楚</div>
      <div class="meta">
        <span class="chip">整理时间：2026-09</span>
        <span class="chip">面向：刚接触 Codex / Cockpit / CPA / 反代 API 的新手</span>
        <span class="chip">目标：把一堆名词还原成「同一个账号的不同属性」</span>
      </div>
    </header>

    <!-- ========== 0 开场 ========== -->
    <div class="note blue">
      <p>第一次接触 Codex、Cockpit、CPA、Sub2API、反代 API 等工具时，是不是经常被一堆名词搞懵？什么 AT、RT、OAuth、已接码、未接码、CPA、Sub2API、Cockpit JSON……</p>
      <p>别急。这些东西看起来复杂，其实只是在描述<strong>账号的不同部分</strong>。只要先搞清楚它们分别是什么，后面的 JSON 转换、账号导入、OAuth 授权、API 配置都会简单很多。</p>
    </div>

    <!-- ========== 1 五句话 ========== -->
    <h2><span class="num">1</span>真正需要记住的只有这五句话</h2>
    <p class="lead">如果你不想看后面那么多内容，先记住下面这五句话就够了。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:200px;">名词</th><th>小白理解</th></tr></thead>
          <tbody>
            <tr><td class="k"><span class="tag t-bal">Plus / Team / K12</span></td><td>这是<b>账号套餐</b></td></tr>
            <tr><td class="k"><span class="tag t-warn">已接码 / 未接码</span></td><td>这是<b>手机验证状态</b></td></tr>
            <tr><td class="k"><span class="tag t-new">AT / RT</span></td><td>这是<b>登录相关的 Token</b></td></tr>
            <tr><td class="k"><span class="tag t-cap">OAuth</span></td><td>这是<b>官方授权登录流程</b></td></tr>
            <tr><td class="k"><span class="tag t-fast">CPA / Sub2API / Cockpit</span></td><td>这是不同软件对应的<b>数据格式 / 使用方式</b></td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note">
      最终可以浓缩成一句话：<b>套餐决定你是什么服务，验证状态说明验证做到哪一步，OAuth 是授权流程，AT / RT 是登录凭证，而 CPA、Sub2API、Cockpit 是不同软件能够识别的数据格式。</b>
    </div>
    <div class="flow">
      <div class="step"><div class="q">把这层关系弄明白</div><div class="a">JSON 转换 → Cockpit 导入 → API 服务 → OAuth 绑定，就不会那么容易混乱了。</div></div>
    </div>

    <!-- ========== 2 先解决最容易混淆的问题 ========== -->
    <h2><span class="num">2</span>先解决最容易混淆的问题</h2>
    <p class="lead">我们先假设有一个账号。一个账号可能同时拥有：一个套餐、一个手机验证状态、一种登录授权方式、一些登录凭证、一份 JSON 文件、一个 JSON 格式。</p>
    <div class="card">
      <ul class="list">
        <li>一个<b>套餐</b>：Plus / Team / K12</li>
        <li>一个<b>手机验证状态</b>：已完成 / 未完成</li>
        <li>一种<b>登录授权方式</b>：OAuth</li>
        <li>一些<b>登录凭证</b>：AT / RT</li>
        <li>一份 <b>JSON 文件</b></li>
        <li>一个 <b>JSON 格式</b>：Cockpit / CPA / Sub2API</li>
      </ul>
    </div>
    <div class="note warn">
      看到这里最容易产生一个误区：<b>“这些是不是不同类型的账号？”</b><br>
      不是。它们描述的是<b>同一个账号的不同方面</b>。
    </div>
    <p class="lead">可以把它想象成去住酒店。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:220px;">账号概念</th><th>酒店例子</th></tr></thead>
          <tbody>
            <tr><td>套餐</td><td>你订的是普通房、豪华房还是套房</td></tr>
            <tr><td>手机验证状态</td><td>你的入住身份验证有没有完成</td></tr>
            <tr><td>Token</td><td>酒店给你的房卡</td></tr>
            <tr><td>OAuth</td><td>通过官方流程完成身份确认并获得授权</td></tr>
            <tr><td>JSON 格式</td><td>把房卡信息装进哪种行李箱，方便哪个软件读取</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note blue">
      所以一定记住：<b>套餐 ≠ 验证状态 ≠ Token ≠ OAuth ≠ JSON 格式</b>。这句话理解了，后面的内容基本就通了。
    </div>

    <!-- ========== 3 AT ========== -->
    <h2><span class="num">3</span>AT 是什么？</h2>
    <p class="lead"><b>AT = Access Token</b>，中文一般叫「访问令牌」。第一次看到 Token，不需要把它想得特别复杂。</p>
    <div class="note">你可以直接把 AT 理解成：<b>账号访问某个服务时使用的一张临时通行证。</b></div>
    <div class="flow">
      <div class="step"><div class="q">账号</div><div class="a">↓ 拿 AT（Access Token）↓ 访问服务</div></div>
    </div>
    <p class="lead">软件在访问服务的时候，需要证明：「我是谁？我有没有权限访问？」AT 就可以承担这个作用。</p>

    <h3>AT 是永久的吗？</h3>
    <p class="lead">通常不是。AT 一般具有有效期，所以：<b>有 AT ≠ 永久可用</b>。如果 AT 过期，而且没有其他能够刷新登录状态的凭证，那么可能就需要重新进行授权或登录。</p>
    <pre class="code"><code>{
  "access_token": "xxxxxx"
}</code></pre>
    <div class="note warn">不要马上认为「这个账号以后永远都能用」。它可能只是保存了当前的访问凭证。</div>

    <!-- ========== 4 RT ========== -->
    <h2><span class="num">4</span>RT 又是什么？</h2>
    <p class="lead"><b>RT = Refresh Token</b>，中文叫「刷新令牌」。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:140px;">Token</th><th>作用</th></tr></thead>
          <tbody>
            <tr><td class="k">AT</td><td>现在使用的通行证，负责当前访问服务</td></tr>
            <tr><td class="k">RT</td><td>通行证过期以后，用来申请新通行证的凭证；在符合认证流程的情况下帮助刷新 AT</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="flow">
      <div class="step"><div class="q">RT 的工作方式</div><div class="a">RT → 刷新 → 新的 AT → 继续使用</div></div>
    </div>

    <!-- ========== 5 AT 与 RT 的区别 ========== -->
    <h2><span class="num">5</span>AT 和 RT 到底有什么区别？</h2>
    <p class="lead">用一个最简单的例子：你去游乐园，工作人员给你一张<b>当天门票</b>，这张门票就是 <b>AT</b>。当天过期以后，如果你还有一种能够证明你拥有有效会员资格的凭证，可以重新换取新的门票，这个东西就类似 <b>RT</b>。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:200px;">Token</th><th>小白理解</th></tr></thead>
          <tbody>
            <tr><td class="k">AT</td><td>当前使用的访问凭证</td></tr>
            <tr><td class="k">RT</td><td>用于刷新访问凭证的凭证</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <p class="lead">这也是为什么：<b>只有 AT</b> 和 <b>AT + RT</b>，并不是完全相同的登录状态。</p>
    <div class="grid g2">
      <div class="vendor">
        <div class="hd"><b>只有 AT</b></div>
        <div class="bd"><div class="mrow"><div class="mdesc">AT 当前可以使用 → AT 过期 → 可能需要重新授权</div></div></div>
      </div>
      <div class="vendor">
        <div class="hd"><b>AT + RT</b></div>
        <div class="bd"><div class="mrow"><div class="mdesc">AT 过期 → 按照认证流程刷新 → 新的 AT → 继续使用</div></div></div>
      </div>
    </div>
    <div class="note warn"><b>⚠️ 注意：</b>没有真实 RT 的账号，并不具备依靠这个 RT 自动续期的能力。</div>

    <!-- ========== 6 为什么有些 JSON 没有 RT ========== -->
    <h2><span class="num">6</span>为什么有些 JSON 里面没有 RT？</h2>
    <p class="lead">这是很多新手第一次看到账号 JSON 时最容易疑惑的问题。例如你可能看到：</p>
    <pre class="code"><code>{
  "access_token": "xxxx",
  "refresh_token": ""
}</code></pre>
    <p class="lead">第一反应可能是：「是不是这个 JSON 坏了？」<b>不一定。</b></p>
    <div class="note">不同登录方式产生的登录数据并不完全一样。某些 Web Session 获取的数据可能并不包含真正的 refresh_token，因此可能出现：<b>access_token 有值、refresh_token 为空</b>。这代表这份数据可能只有当前 Access Token，而不是完整的 OAuth 登录状态。</div>
    <div class="note blue">所以以后看到 <code>refresh_token = 空</code>，先不要慌。</div>

    <!-- ========== 7 OAuth ========== -->
    <h2><span class="num">7</span>OAuth 到底是什么？</h2>
    <p class="lead">这是另一个非常容易把小白绕进去的名词。OAuth 可以简单理解成：<b>通过官方登录授权流程，让第三方工具获得经过授权的访问权限。</b></p>
    <p class="lead">你不需要一开始就研究 OAuth 的协议细节，先理解这个流程：</p>
    <div class="flow">
      <div class="step"><div class="q">Cockpit</div><div class="a">发起 OAuth 授权</div></div>
      <div class="step"><div class="q">打开官方登录页面</div><div class="a">登录账号</div></div>
      <div class="step"><div class="q">完成官方授权</div><div class="a">Cockpit 获得相应登录状态</div></div>
    </div>
    <div class="note blue">所以：<b>OAuth 更像是一套「官方授权流程」</b>，而 <b>AT / RT 更像是授权流程之后产生或使用的凭证</b>。这两个概念不要混为一谈。</div>

    <!-- ========== 8 OAuth / AT / RT 一起理解 ========== -->
    <h2><span class="num">8</span>OAuth、AT、RT 怎么一起理解？</h2>
    <p class="lead">把它们放到一起就非常清楚了：</p>
    <pre class="code"><code>             OAuth
               │
        官方授权流程
               │
               ↓
          完成授权
               │
               ↓
          获得登录状态
               │
          ┌────┴────┐
          ↓         ↓
         AT         RT
          │         │
      当前访问    刷新凭证</code></pre>
    <div class="note warn">所以不要把它们理解成 <code>OAuth = AT</code>、<code>OAuth = RT</code>。它们不是同一种东西。</div>

    <!-- ========== 9 已接码 / 未接码 ========== -->
    <h2><span class="num">9</span>「已接码」和「未接码」是什么意思？</h2>
    <p class="lead">这个词在一些账号商品或教程中非常常见。</p>
    <div class="note warn"><b>⚠️ 「接码」并不是 OpenAI 官方的技术名称。</b>在相关教程语境下，更准确地说，可以理解成：<b>手机验证状态</b>。</div>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:150px;">说法</th><th>在相关教程语境下的含义</th></tr></thead>
          <tbody>
            <tr>
              <td class="k"><span class="tag t-fast">已接码</span></td>
              <td>这个账号之前已经完成过相关登录流程中要求的手机号验证。如果账号本身授权状态正常，那么后续继续进行 OAuth 登录时，通常可以直接进入后面的授权流程。</td>
            </tr>
            <tr>
              <td class="k"><span class="tag t-warn">未接码</span></td>
              <td>这个账号还没有完成相关的手机号验证。如果你进行 OAuth 登录时，官方页面要求 Add phone / 添加手机号 / 手机验证，那么就需要按照官方页面完成验证。</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="flow">
      <div class="step"><div class="q">未接码的流程</div><div class="a">登录 → 发现需要手机号验证 → 完成手机号验证 → 继续 OAuth 授权 → 登录完成</div></div>
    </div>
    <div class="note">这里一定要注意：<b>是否要求手机号验证，会受到官方当前认证流程和账号状态影响。</b>并不是所有账号、每次登录都会出现手机号验证。同时：<b>JSON 转换本身无法绕过官方的验证步骤。</b></div>

    <!-- ========== 10 已接码不是所有服务都必须 ========== -->
    <h2><span class="num">10</span>「已接码」是不是所有服务都必须？</h2>
    <p class="lead">不要这么理解。例如某些商品可能会写：<code>反代 Plus｜已接码</code>。这里的「已接码」首先是：<b>这个商品交付时对应的手机验证状态</b>。</p>
    <p class="lead">它和「所有账号都必须已接码」不是一个概念。更准确的理解应该是：</p>
    <div class="note blue"><b>如果当前官方 OAuth 流程要求进行手机号验证，那么必须先按照官方流程完成验证，OAuth 授权才能继续。</b></div>
    <div class="note warn">所以：<b>商品描述 ≠ 官方技术规范</b>。不要把商品名称直接理解成技术规范。</div>

    <!-- ========== 11 什么是 JSON 格式 ========== -->
    <h2><span class="num">11</span>什么是 JSON 格式？</h2>
    <p class="lead">现在终于来到很多小白最容易懵的地方：「为什么我的 JSON 不能直接导入？」原因其实很简单：<b>JSON 只是数据的载体。</b>不同软件需要的数据结构可能不同。</p>
    <p class="lead">举个简单例子。同样是一个人的信息：</p>
    <div class="grid g2">
      <div class="vendor">
        <div class="hd"><b>软件 A 要的</b></div>
        <div class="bd"><pre class="code in-card"><code>{
  "name": "张三",
  "age": 20
}</code></pre></div>
      </div>
      <div class="vendor">
        <div class="hd"><b>软件 B 要的</b></div>
        <div class="bd"><pre class="code in-card"><code>{
  "username": "张三",
  "user_age": 20
}</code></pre></div>
      </div>
    </div>
    <div class="note">数据表达的是类似的东西，但是<b>格式不同</b>，所以软件可能无法直接识别。</div>

    <!-- ========== 12 为什么需要 JSON 转换 ========== -->
    <h2><span class="num">12</span>为什么需要「JSON 转换」？</h2>
    <p class="lead">假设你手里有一份原始账号数据，本质上转换就是在做：<b>把同一份认证数据整理成目标软件能够识别的结构。</b></p>
    <div class="flow">
      <div class="step"><div class="q">目标：Cockpit</div><div class="a">原始 JSON → 转换 → Cockpit JSON</div></div>
      <div class="step"><div class="q">目标：CPA</div><div class="a">原始 JSON → 转换 → CPA JSON</div></div>
      <div class="step"><div class="q">目标：Sub2API</div><div class="a">原始 JSON → 转换 → Sub2API JSON</div></div>
    </div>
    <div class="note blue">所以一定要记住：<b>JSON 格式不是账号本身。</b></div>

    <!-- ========== 13 CPA ========== -->
    <h2><span class="num">13</span>CPA 是什么？</h2>
    <p class="lead">这里的 CPA 通常指：<b>CLIProxyAPI</b>。它是一类代理 / API 服务工具，可以处理包括 Codex OAuth 等认证信息。</p>
    <div class="note">因此：<b>CPA 格式</b>可以简单理解为：<b>CLIProxyAPI 能够识别的认证文件结构。</b></div>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:120px;">说法</th><th>示例</th></tr></thead>
          <tbody>
            <tr><td><span class="tag t-warn">❌ 不太准确</span></td><td>“这是一个 CPA 账号。”</td></tr>
            <tr><td><span class="tag t-fast">✅ 更准确</span></td><td>“这是 CPA 格式的 JSON。”</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 14 Sub2API ========== -->
    <h2><span class="num">14</span>Sub2API 又是什么？</h2>
    <p class="lead">道理完全一样。<b>Sub2API 格式不是一种账号。</b>它指的是：为了让 Sub2API 能够识别和导入，而整理出来的一种 JSON 数据结构。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:120px;">说法</th><th>示例</th></tr></thead>
          <tbody>
            <tr><td><span class="tag t-warn">❌ 不太准确</span></td><td>“这是一个 Sub2API 账号。”</td></tr>
            <tr><td><span class="tag t-fast">✅ 更准确</span></td><td>“这是 Sub2API 格式的 JSON。” / “这份 JSON 可以导入 Sub2API。”</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 15 Cockpit JSON ========== -->
    <h2><span class="num">15</span>Cockpit JSON 又是什么？</h2>
    <p class="lead">如果你最终要使用 Cockpit，那么最简单的理解方式就是：</p>
    <div class="flow">
      <div class="step"><div class="q">原始 JSON</div><div class="a">↓ 转换 ↓ 选择 Cockpit 格式 ↓ 得到 Cockpit JSON ↓ 导入 Cockpit</div></div>
    </div>
    <div class="note blue">你不需要为了使用 Cockpit，先把 CPA、Sub2API 的内部结构全部研究一遍。只需要记住：<b>你用哪个软件，就准备哪个软件能够识别的格式。</b></div>

    <!-- ========== 16 概念总图 ========== -->
    <h2><span class="num">16</span>把所有概念放到一张图里</h2>
    <p class="lead">现在我们把前面所有名词放在一起。假设有一个账号：</p>
    <pre class="code"><code>┌──────────────────────────────┐
│            一个账号           │
├──────────────────────────────┤
│            套餐               │
│       Plus / Team / K12       │
├──────────────────────────────┤
│          手机验证状态          │
│        已完成 / 未完成         │
├──────────────────────────────┤
│            授权方式            │
│             OAuth             │
├──────────────────────────────┤
│            登录凭证            │
│            AT / RT             │
├──────────────────────────────┤
│          JSON 数据格式         │
│    Cockpit / CPA / Sub2API    │
└──────────────────────────────┘</code></pre>
    <div class="note">这时候是不是清楚很多了？它们其实是在描述：<b>同一个账号的不同属性。</b></div>

    <!-- ========== 17 最该记住的一句话 ========== -->
    <h2><span class="num">17</span>小白最应该记住的一句话</h2>
    <div class="note blue big">格式 ≠ 账号 ≠ Token ≠ 验证状态</div>
    <p class="lead">进一步拆开就是：</p>
    <div class="flow">
      <div class="step"><div class="q">Plus / Team / K12</div><div class="a">↓ 账号套餐</div></div>
      <div class="step"><div class="q">已完成 / 未完成</div><div class="a">↓ 手机验证状态</div></div>
      <div class="step"><div class="q">AT / RT</div><div class="a">↓ 登录凭证</div></div>
      <div class="step"><div class="q">OAuth</div><div class="a">↓ 官方授权流程</div></div>
      <div class="step"><div class="q">CPA / Sub2API / Cockpit</div><div class="a">↓ 软件需要的 JSON 格式</div></div>
    </div>
    <div class="note">这样以后看到商品介绍或者教程里的各种名词，就不会再全部混在一起。</div>

    <!-- ========== 18 怎么判断 ========== -->
    <h2><span class="num">18</span>小白到底应该怎么判断？</h2>
    <p class="lead">其实非常简单，对号入座就行。</p>
    <div class="flow">
      <div class="step"><div class="q">情况一：我要用 Cockpit</div><div class="a">你关注 <b>Cockpit 格式</b></div></div>
      <div class="step"><div class="q">情况二：我要用 Sub2API</div><div class="a">你关注 <b>Sub2API 格式</b></div></div>
      <div class="step"><div class="q">情况三：我要用 CLIProxyAPI / CPA</div><div class="a">你关注 <b>CPA 格式</b></div></div>
      <div class="step"><div class="q">情况四：我看到一个 AT</div><div class="a">说明目前拥有一个 Access Token。但不代表一定拥有完整、可自动刷新的 OAuth 登录状态。</div></div>
      <div class="step"><div class="q">情况五：我看到 AT + RT</div><div class="a">说明除了当前访问凭证之外，还存在 Refresh Token，可用于符合认证流程的 Token 刷新。</div></div>
      <div class="step"><div class="q">情况六：商品写「已接码」</div><div class="a">按照商品说明理解为：对应的手机验证状态已经完成。</div></div>
      <div class="step"><div class="q">情况七：商品写「未接码」</div><div class="a">理解为：后续进行 OAuth 登录时，如果官方要求手机号验证，需要按照官方页面完成验证。</div></div>
    </div>

    <!-- ========== 19 总表 ========== -->
    <h2><span class="num">19</span>最后用一张表彻底记住</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:170px;">名词</th><th>小白理解</th><th style="width:170px;">它属于什么</th></tr></thead>
          <tbody>
            <tr><td class="k">Plus</td><td>一种套餐</td><td><span class="tag t-bal">账号套餐</span></td></tr>
            <tr><td class="k">Team</td><td>一种套餐</td><td><span class="tag t-bal">账号套餐</span></td></tr>
            <tr><td class="k">K12</td><td>一种套餐</td><td><span class="tag t-bal">账号套餐</span></td></tr>
            <tr><td class="k">已接码</td><td>手机验证已经完成</td><td><span class="tag t-warn">验证状态</span></td></tr>
            <tr><td class="k">未接码</td><td>手机验证可能尚未完成</td><td><span class="tag t-warn">验证状态</span></td></tr>
            <tr><td class="k">AT</td><td>当前访问服务的凭证</td><td><span class="tag t-new">Token</span></td></tr>
            <tr><td class="k">RT</td><td>用于刷新 Token 的凭证</td><td><span class="tag t-new">Token</span></td></tr>
            <tr><td class="k">OAuth</td><td>官方授权流程</td><td><span class="tag t-cap">授权方式</span></td></tr>
            <tr><td class="k">CPA</td><td>CLIProxyAPI</td><td><span class="tag t-fast">工具 / 服务</span></td></tr>
            <tr><td class="k">CPA JSON</td><td>CPA 能识别的数据格式</td><td><span class="tag t-mini">文件格式</span></td></tr>
            <tr><td class="k">Sub2API JSON</td><td>Sub2API 能识别的数据格式</td><td><span class="tag t-mini">文件格式</span></td></tr>
            <tr><td class="k">Cockpit JSON</td><td>Cockpit 能识别的数据格式</td><td><span class="tag t-mini">文件格式</span></td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 20 脑图 ========== -->
    <h2><span class="num">20</span>最后再浓缩成一张「脑图」</h2>
    <p class="lead">如果你只想记住最核心的关系，可以直接看这一张：</p>
    <pre class="code"><code>                         一个账号
                            │
            ┌───────────────┼───────────────┐
            │               │               │
            ↓               ↓               ↓
          套餐           验证状态         授权 / 登录
            │               │               │
     Plus / Team / K12  已接码 / 未接码     OAuth
                                            │
                                            ↓
                                        登录凭证
                                        ┌───┴───┐
                                        ↓       ↓
                                       AT      RT
                                    Access   Refresh
                                    Token     Token


                         登录数据
                            │
                            ↓
                         JSON
                            │
            ┌───────────────┼───────────────┐
            ↓               ↓               ↓
        Cockpit JSON     CPA JSON      Sub2API JSON
            │               │               │
            ↓               ↓               ↓
         Cockpit        CLIProxyAPI      Sub2API</code></pre>

    <!-- ========== 21 一句话总结 ========== -->
    <h2><span class="num">21</span>一句话总结</h2>
    <div class="note blue">
      <p><b>套餐决定账号提供什么服务，验证状态说明手机号验证到了哪一步，OAuth 是官方授权流程，AT / RT 是登录过程中使用的凭证，而 Cockpit、CPA、Sub2API 则是不同工具对这些数据的组织和使用方式。</b></p>
    </div>
    <p class="lead">当你把这几个概念分开之后：</p>
    <div class="flow">
      <div class="step"><div class="q">概念链路</div><div class="a">账号 → 验证 → OAuth → AT / RT → JSON → 对应工具 → API 服务</div></div>
    </div>
    <p class="lead">后面再看到各种教程里的「转 JSON」「导入 Cockpit」「CPA 格式」「Sub2API 格式」「OAuth 绑定」，就不会再觉得它们是一堆互相没有关系的名词了。</p>

    <!-- ========== 口诀 ========== -->
    <div class="slogan">
      <div class="slogan-title">💡 新手记忆口诀</div>
      <div class="slogan-body">
        套餐看服务，验证看状态；<br>
        OAuth 管授权，AT / RT 管凭证；<br>
        JSON 看格式，工具看兼容。
      </div>
    </div>

    <footer>
      本文为新手向概念科普整理，用于帮助区分「套餐 / 验证状态 / Token / 授权流程 / JSON 格式」这几类名词。<br>
      其中「接码」等说法为教程与商品语境下的通俗叫法，并非官方技术术语；账号的实际验证要求、授权流程与可用状态，请以官方页面当前提示为准。
    </footer>
  </div>
</template>

<script>
export default {
  name: 'GptAcountKnowledge'
}
</script>

<style scoped>
  .wrap {
    /* 撑满内容区（居中限宽时左右留白由 margin 提供，内边距由内容区统一提供） */
    height: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 12px;
    background: var(--surface-1);
    color: #1a1d21;
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 8px;
  }
  .wrap * {
    box-sizing: border-box;
  }
  .wrap code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--surface-2);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
  }
  header {
    margin-bottom: 20px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  }
  h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 8px;
    letter-spacing: -0.4px;
    color: #1a1d21;
  }
  .sub {
    color: #5a6270;
    font-size: 14px;
  }
  .meta {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .chip {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    color: #5a6270;
  }

  h2 {
    font-size: 19px;
    font-weight: 650;
    margin: 40px 0 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: -0.2px;
    color: #1a1d21;
  }
  h2 .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: #eef3fe;
    color: #2f6fed;
    font-size: 13px;
    font-weight: 700;
    flex: none;
  }
  h3 {
    font-size: 15px;
    font-weight: 650;
    margin: 20px 0 6px;
    color: #1a1d21;
  }
  .lead {
    color: #5a6270;
    margin: 0 0 16px;
    font-size: 13.5px;
  }
  .lead:last-child {
    margin-bottom: 0;
  }

  .card {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
    overflow: hidden;
  }
  .table-box {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  thead th {
    background: var(--surface-1);
    text-align: left;
    font-weight: 600;
    color: #5a6270;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    white-space: nowrap;
    font-size: 12.5px;
  }
  tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    vertical-align: top;
    color: #3a414c;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover {
    background: var(--surface-1);
  }
  td.k {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    white-space: nowrap;
    color: #1a1d21;
  }

  .list {
    margin: 0;
    padding: 14px 18px 14px 34px;
    font-size: 13.5px;
    color: #3a414c;
  }
  .list li {
    margin: 3px 0;
  }

  .tag {
    display: inline-block;
    font-size: 11.5px;
    padding: 2px 8px;
    border-radius: 5px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.6;
  }
  .t-bal {
    background: #eef3fe;
    color: #2f6fed;
  }
  .t-fast {
    background: #e9f7ef;
    color: #12a150;
  }
  .t-mini {
    background: var(--surface-2);
    color: #5a6270;
  }
  .t-cap {
    background: #f2edfd;
    color: #7048c4;
  }
  .t-new {
    background: #fdf3e3;
    color: #c77700;
  }
  .t-warn {
    background: #fdecec;
    color: #d93a3a;
  }

  .grid {
    display: grid;
    gap: 14px;
    margin-top: 4px;
  }
  .g2 {
    grid-template-columns: repeat(2, 1fr);
  }
  .vendor {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
    overflow: hidden;
    min-width: 0;
  }
  .vendor .hd {
    padding: 13px 16px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    background: var(--surface-1);
  }
  .vendor .hd b {
    font-size: 14.5px;
    color: #1a1d21;
  }
  .vendor .bd {
    padding: 4px 0;
  }
  .mrow {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .mrow:last-child {
    border-bottom: none;
  }
  .mdesc {
    color: #5a6270;
    font-size: 13px;
  }

  .code {
    background: #1e2229;
    color: #e6e8eb;
    border-radius: 10px;
    padding: 14px 16px;
    margin: 0 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.65;
    white-space: pre;
  }
  .code code {
    background: transparent;
    padding: 0;
    color: inherit;
    font-size: inherit;
  }
  .code.in-card {
    margin: 12px;
    border-radius: 8px;
  }

  .note {
    background: #fffdf5;
    border: 1px solid #f0e2c0;
    border-left: 3px solid #e0a800;
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    font-size: 13px;
    color: #5c4a15;
    margin-bottom: 16px;
  }
  .note b {
    color: #3d3110;
  }
  .note p {
    margin: 0 0 8px;
  }
  .note p:last-child {
    margin-bottom: 0;
  }
  .note.blue {
    background: #eef3fe;
    border-color: #c9dbfb;
    border-left-color: #2f6fed;
    color: #1e3d73;
  }
  .note.blue b {
    color: #14305c;
  }
  .note.warn {
    background: #fdf3f3;
    border-color: #f6d5d5;
    border-left-color: #d93a3a;
    color: #7a2323;
  }
  .note.warn b {
    color: #5c1414;
  }
  .note.big {
    font-size: 17px;
    font-weight: 700;
    text-align: center;
    padding: 18px 16px;
    letter-spacing: 0.2px;
  }

  .flow {
    display: flex;
    flex-direction: column;
    margin: 0 0 16px;
  }
  .step {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 16px;
    border: 1px solid rgba(31, 45, 61, 0.06);
    background: var(--surface-1);
  }
  .step:first-child {
    border-radius: 12px 12px 0 0;
  }
  .step:last-child {
    border-radius: 0 0 12px 12px;
  }
  .step:only-child {
    border-radius: 12px;
  }
  .step + .step {
    border-top: none;
  }
  .step .q {
    font-weight: 600;
    min-width: 180px;
    flex: none;
    font-size: 13.5px;
    color: #1a1d21;
  }
  .step .a {
    color: #5a6270;
    font-size: 13px;
  }

  .slogan {
    margin-top: 32px;
    padding: 22px 20px;
    border-radius: 14px;
    background: linear-gradient(135deg, #eef3fe 0%, #f6f2fd 100%);
    border: 1px solid #dde5f8;
    text-align: center;
  }
  .slogan-title {
    font-size: 13px;
    font-weight: 700;
    color: #2f6fed;
    margin-bottom: 10px;
    letter-spacing: 0.3px;
  }
  .slogan-body {
    font-size: 16px;
    line-height: 2;
    font-weight: 650;
    color: #1a1d21;
    letter-spacing: 0.4px;
  }

  footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid rgba(31, 45, 61, 0.06);
    color: #8b939f;
    font-size: 12.5px;
    line-height: 1.8;
  }

  @media (max-width: 820px) {
    .g2 {
      grid-template-columns: 1fr;
    }
  }
  @media (max-width: 640px) {
    .wrap {
      padding: 16px;
    }
    .step {
      flex-direction: column;
      gap: 3px;
    }
    .step .q {
      min-width: 0;
    }
    .mname {
      min-width: 0;
    }
    h1 {
      font-size: 22px;
    }
    h2 {
      font-size: 17px;
    }
    .slogan-body {
      font-size: 14px;
    }
  }
</style>
